import { hasPermission, Permission } from 'core/auth/rbac'
import { AdminLayout } from 'core/components/Layout/AdminLayout'
import { ProtectedRoute } from 'features/auth/presentation/components/ProtectedRoute'
import { PublicOnlyRoute } from 'features/auth/presentation/components/PublicOnlyRoute'
import { useAuth } from 'features/auth/presentation/context/AuthContext'
import { LoginPage } from 'features/auth/presentation/pages/LoginPage'
import { CataloguePage } from 'features/catalogue/presentation/pages/CataloguePage'
import { DashboardPage } from 'features/dashboard/presentation/pages/DashboardPage'
import { FarmerSalesPage } from 'features/farmer-sales/presentation/pages/FarmerSalesPage'
import { GrievanceLedgerPage } from 'features/grievances/presentation/pages/GrievanceLedgerPage'
import { GrievancesPage } from 'features/grievances/presentation/pages/GrievancesPage'
import { KycVerificationPage } from 'features/kyc/presentation/pages/KycVerificationPage'
import { NotificationsPage } from 'features/notifications/presentation/pages/NotificationsPage'
import { IndentsPage } from 'features/orders/presentation/pages/IndentsPage'
import { OrdersPage } from 'features/orders/presentation/pages/OrdersPage'
import { PaymentsPage } from 'features/payments/presentation/pages/PaymentsPage'
import { SettingsPage } from 'features/settings/presentation/pages/SettingsPage'
import { PrivacyPolicyPage } from 'features/legal/presentation/pages/PrivacyPolicyPage'
import { TermsOfServicePage } from 'features/legal/presentation/pages/TermsOfServicePage'
import { UserManagementPage } from 'features/user-management/presentation/pages/UserManagementPage'
import { Navigate, Route, Routes } from 'react-router-dom'

const ProtectedElement = ({ permission, children }: { permission: Permission; children: React.ReactNode }) => {
  const { user } = useAuth()
  if (!hasPermission(user, permission)) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

export const AppRoutes = () => (
  <Routes>
    {/* Public Access Routes: redirects to /dashboard if already authenticated */}
    <Route element={<PublicOnlyRoute />}>
      <Route path="/login" element={<LoginPage />} />
    </Route>

    {/* Public Compliance Legal Pages (accessible without login) */}
    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
    <Route path="/terms-of-service" element={<TermsOfServicePage />} />

    {/* Protected Portal Layout Shell */}
    <Route element={<ProtectedRoute />}>
      <Route element={<AdminLayout />}>
        {/* Index redirect to /dashboard */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />

        {/* Protected views with RBAC permissions */}
        <Route
          path="/user-management"
          element={
            <ProtectedElement permission="view_user_management">
              <UserManagementPage />
            </ProtectedElement>
          }
        />
        <Route
          path="/kyc-verification"
          element={
            <ProtectedElement permission="view_kyc">
              <KycVerificationPage />
            </ProtectedElement>
          }
        />
        <Route
          path="/catalogue"
          element={
            <ProtectedElement permission="view_catalogue">
              <CataloguePage />
            </ProtectedElement>
          }
        />
        <Route
          path="/orders"
          element={
            <ProtectedElement permission="view_orders">
              <OrdersPage />
            </ProtectedElement>
          }
        />
        <Route
          path="/indents"
          element={
            <ProtectedElement permission="view_orders">
              <IndentsPage />
            </ProtectedElement>
          }
        />
        <Route
          path="/payments"
          element={
            <ProtectedElement permission="view_payments">
              <PaymentsPage />
            </ProtectedElement>
          }
        />

        <Route
          path="/grievances"
          element={
            <ProtectedElement permission="view_grievances">
              <GrievancesPage />
            </ProtectedElement>
          }
        />
        <Route
          path="/grievances/:id/ledger"
          element={
            <ProtectedElement permission="view_grievances">
              <GrievanceLedgerPage />
            </ProtectedElement>
          }
        />
        <Route
          path="/farmer-sales"
          element={
            <ProtectedElement permission="view_farmer_sales">
              <FarmerSalesPage />
            </ProtectedElement>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedElement permission="view_settings">
              <SettingsPage />
            </ProtectedElement>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedElement permission="view_orders">
              <NotificationsPage />
            </ProtectedElement>
          }
        />
      </Route>
    </Route>

    {/* Wildcard Fallback */}
    <Route path="*" element={<Navigate to="/dashboard" replace />} />
  </Routes>
)
